import React, { useState, useEffect, useMemo } from "react";
import {
    FormControl, TextField, Typography, Select, MenuItem
} from "@mui/material";
import jsep from 'jsep';
import jsepObject from '@jsep-plugin/object';
jsep.plugins.register(jsepObject);

const opPrec = {"+":7,"-":7,"*":6,"**":4,"/":4,"//":4,">":12,"<":12,">=":12,"<=":12,                
                "!=":12,"==":12,"&&":14,"||":15,"&":9,"|":11,"^":10 };
//"!":13
Object.keys(opPrec).forEach(op => { jsep.addBinaryOp(op, opPrec[op])});

//example of a predefined expr:
//   { name: "TEST", expr: 'CRON("*/30 * * * *", tav(TAG(P("source_tag", "_tagname", "tag"), 1000 * P("time", 1800, "int"), 0), 1800000, []))',
//   description: "There would be some help text here that could guide the user in how to set up the values for this custom choice."}

function stringToExprWithCron(estr) {
    const ret = stringToExpr(estr);
    return (ret.n === 'CRON') ? {"cron": ret.a[0], ...ret.a[1] } : ret;
}
function extract_tagname(s) { // if options contains N_<tagname>
    return s.match(/\$N_[^$]+/g)?.[0]?.slice(3);
}
function stringToExpr(estr) {
    let parse;
    parse = jsep(estr);
    //console.log("JSEP",estr, parse);
    function jnode(pnode, litmode) {
        function optarg(d) {
            if(d.a.length === 0) delete d.a;
            return d;
        }
        if(pnode.type === 'BinaryExpression') {
            return {
                'n':pnode.operator,
                'a':[jnode(pnode.left),jnode(pnode.right)]
            }
        } else if(pnode.type === 'CallExpression') {
            const nm = pnode.callee.name.split("$")
            const opts = (nm.length > 1) ? {'o': nm.slice(1)} : {}
            if(nm[0] === 'TAG') {
                const tag = jnode(pnode.arguments[0]);
                //todo: (maybe don't enforce this) check for string or param value (for legal tag)
                //if(typeof(tag) !== 'string' && tag?.n !== 'P' ) throw new Error("illegal tag name");
                return optarg({
                    ...opts,
                    'tag': tag,
                    'a': pnode.arguments.slice(1).map(jnode)
                })
            } else if(nm[0] === 'LITERAL') {
                return optarg({                        
                    'n':'lit',
                    'a': pnode.arguments
                })
            }
            return optarg({
                ...opts,                    
                'n':nm[0],
                'a':pnode.arguments.map(jnode)
            })
        //} else if(pnode.type === 'Identifier') {
        //   return { 'n':pnode.name }
        } else if(pnode.type === 'ObjectExpression') {
            function addprop(d, p) {
                //todo check key name and value to make sure literals or other acceptable non-compute type
                return d
            }
            const ret = pnode.properties.reduce(addprop, {})
            return litmode ? ret : { 'n':'lit', 'a': ret }
        } else if(pnode.type === 'ArrayExpression') {
            return pnode.elements.map(jnode)
        } else if(pnode.type === 'UnaryExpression' && pnode.operator === '-') {
            if(pnode.argument.type === 'Literal') {
                return -pnode.argument.value 
            } else {
                return {
                    'n': '-',
                    'a':[0, jnode(pnode.argument)]
                }
            }
        } else if(pnode.type === 'Literal') {
            if(typeof(pnode.value) !== "object") { //todo: was Object
                return pnode.value
            } else {
                return {
                    'n':'lit',
                    'a': pnode.value
                }
            }
        } else {
            throw new Error("unknown type:"+JSON.stringify(pnode))
        }
    }
    return jnode(parse);
}

function exprToStringWithCron(jexpr, extractParams) {
    const ret = exprToString(jexpr, extractParams);
    return jexpr.cron ? 'CRON("'+jexpr.cron+'", '+ret+")" : ret;
}

function exprToString(jexpr, extractParams, pprec, prassoc) {
    function exprOptions(opts) {
        return (opts && opts.length) ? "$"+opts.join("$") : "";
    }
    if(typeof(jexpr) === 'object' && !Array.isArray(jexpr)) {
        if(jexpr.n) {
            const prec = opPrec[jexpr.n]
            const rassoc = (jexpr.n === '**') ? 1 : 0 //only right assoc we have
            if(jexpr.n === 'lit') {
                return "LITERAL("+JSON.stringify(jexpr.a)+")";
            } else if(jexpr.a?.length === 2 && prec) { //operator
                const l = exprToString(jexpr.a[0], extractParams, prec, 0);
                const r = exprToString(jexpr.a[1], extractParams, prec, 1);
                if(pprec && !(prec > pprec) &&
                   (prec < pprec || rassoc !== prassoc))
                    return '(' + l + ' ' + jexpr.n + ' ' + r + ')';
                return l + ' ' + jexpr.n + ' ' + r;
            } else { //function
                if(extractParams != null && jexpr.n === 'P') {
                    const pkey = jexpr.a[0]; //error if not a simple string
                    extractParams[pkey] = [jexpr.a[1], jexpr.a[2]];
                }
                return jexpr.n+exprOptions(jexpr.o)+'('+jexpr.a.map(xx => exprToString(xx, extractParams)).join(", ") + ')';
            }
        } else if(jexpr.tag) {
            const a = [jexpr.tag, ...jexpr.a].map(xx => exprToString(xx, extractParams)).join(", ")
            const tagexpr = "TAG"+exprOptions(jexpr.o)
            return tagexpr+'('+a+')'
        }
        // console.log("NODE", jexpr)
        throw new Error("bad node: "+JSON.stringify(jexpr))
    }
    return JSON.stringify(jexpr);
}

function TagChooser({
    index,
    value,
    param, // [default, type]
    readOnly,
    handleParamChange
}) {
    const und = value?.indexOf("_");
    const und1 = param[0]?.indexOf("_");    
    const stack = value?.slice(0,und) || "";
    const defStack = param[0]?.slice(0,und1);    
    const tag = value?.slice(und+1) || "";
    const defTag = param[0]?.slice(und1+1);        
    const setTag = (v) => {
        if (/^[a-zA-Z0-9_]*$/.test(v)) {
            const n = (stack||defStack)+"_"+(v||defTag);
            console.log("SET TAG", v, n);
            handleParamChange(index, (n !== "_" && n !== param[0]) ? n : "");
        }  
    };
    const setStack = (v) => {
        const n = (v||defStack)+"_"+(tag||defTag);
        console.log("SET stack", v, n);        
        handleParamChange(index, (n !== "_" && n !== param[0]) ? n : "");        
    };
    return (<tr>
               <td> <Typography variant="subtitle2" style={{ fontSize: 10 }}> {index} </Typography></td>
               <td>
                   <TextField
                       variant="standard"
                       type="string"
                       value={ stack }
                       disabled={readOnly}
                       placeholder={ defStack || "<same stack>" }
                       InputLabelProps={{ sx: { fontSize: 10 } }}
                       InputProps={{ sx: { fontSize: 10 } }}
                       onChange={(event) => setStack(event.target.value)}
                   />
               </td>
               <td>
                   <TextField
                       variant="standard"
                       type="string"
                       value={ tag }
                       disabled={readOnly}                       
                       placeholder={ "default tag name: " + (defTag || "<no default>") }                             
                       InputLabelProps={{ sx: { fontSize: 10 } }}
                       InputProps={{ sx: { fontSize: 10 } }}
                       onChange={(event) => setTag(event.target.value)}
                   />
               </td>
           </tr>);
}

function ParamChooser(props) {
    const { readOnly, value, index, param, // [value, default, type]
            handleParamChange } = props;
    return ((param?.[1] === 'tag') ?
        <TagChooser {...props} /> : 
            <tr>
                <td> <Typography variant="subtitle2" style={{ fontSize: 10 }}> {index} </Typography></td>
                <td>
                    <TextField
                        variant="standard"
                        type="string"
                        value={ "" + (value || "") }
                        placeholder={ "" + (param[0] || "") }
                        InputLabelProps={{ sx: { fontSize: 10 } }}
                        InputProps={{ sx: { fontSize: 10 } }}
                        disabled={ readOnly }                    
                        onChange={(event) => handleParamChange(index, event.target.value)}
                    />
                </td>
            </tr>); 
}
            
//Notes on how this works:
//  This component takes the JSON expr form ('derived') which is a parse tree in json from of expression.
//  That is not very user friendly, so there is a bunch of code here that parses that into a string form
//    that is like an excel formula.  That string form ('parsed') is what the user can edit directly - and
//    then it is unparsed to the JSON form and setDerived is called.
//  Now - there are some predefined settings: ('predefined') that are parameterized.  The params use the "P"
//    function that can be defined as "params" in a expression node (usually at the top)
//      P( name, default_value, optional_type )

const TagValueCalculation = ({
  derived,
  setDerived,
  readOnly,
  predefined,
  advanced = false
}) => {
    const setDerivedIfChanged = useMemo(() => {
        return (v) => {
            const oldV = derived || "";
            console.log("OLD:", oldV, "NEW:", v);
            if(v != oldV) setDerived(v);
        }
    }, [derived, setDerived]);
    //todo also match the choice if there params and tag params are different values
    const [parseError, setParseError] = useState(false);
    const [parsed, setParsed] = useState();
    const [choice, setChoice] = useState('unknown'); //whichChoice(derived));
    const [depParams, setDepParams] = useState({}); // name -> { default, type }
    const [editDerived, setEditDerived] = useState(derived);
    const derivedParams = useMemo(() => { //todo what do we do if we can't parse
        try {
            return JSON.parse(derived)?.params || {};
        } catch {
            return {}
        }
    }, [derived]);
    const handleParamChange = (k, v) => {
        function processVal(v, args) {
            if(args?.[1] === "int") return parseInt(v);
            else if(args?.[1] === "number") return Number(v);
            return v;
        }
        const newParams = {...derivedParams}
        if(v == null) {
            delete newParams[k];
        } else {
            newParams[k] = processVal(v, depParams[k]);
        }
        setDerivedIfChanged(JSON.stringify({...JSON.parse(derived), params: newParams}));
    };
    useEffect(() => {
        console.log("UE DERIVED", derived, choice);
        if(derived) {
            try {
                const expr = JSON.parse(derived);
                const availableParams = {}
                const p = exprToStringWithCron(expr, availableParams);
                const ch = ((d) => {
                    for(let i=0; i<predefined.length; i++) {
                        if(predefined[i].expr === d) return i;
                    }
                    return "custom";
                }) (p);
                
                if(ch != choice) setChoice(ch);
                console.log("setting  parsed...", derived, JSON.stringify(p), ch, choice);
                if(p != parsed) {
                    setParsed(p);
                    if(parseError) setParseError(false);
                }
                setDepParams(availableParams);
            } catch (err) {
                console.log("DER ERR", err);
                setDepParams({});
            } 
        } else {
            if(choice != "From Device") {
                console.log("SET CHOICE")
                setChoice("From Device"); // todo: decide if "" instead
                setDepParams({});                
            }
        }
        setEditDerived(derived);
    }, [derived]);
    function changeParsed(newParsed) {
        try {
            const newExprNoParams = (newParsed == null || newParsed === "") ? "" : stringToExprWithCron(newParsed);
            console.log("RESULT COMPILE", newExprNoParams);
            const newExpr = (derivedParams && Object.keys(derivedParams).length > 0) ? {...newExprNoParams, params: derivedParams } : newExprNoParams;
            const newDerived = newExpr === "" ? "" : JSON.stringify(newExpr);
            //double encode since JSON dump options might have been different
            const oldDerived = derived ? JSON.stringify(JSON.parse(derived)) : "";
            if(oldDerived !== newDerived) {
                console.log("set derived again")
                console.log(JSON.stringify(oldDerived));
                console.log(JSON.stringify(newDerived));
                setDerivedIfChanged(newDerived);
            }
            if(parseError) setParseError(false);
        } catch(err) {
            console.log(err);
            setParseError(true);
            setParsed(newParsed);
        }
    }
    function chooseChoice(ch) {
        console.log("CHOOSING", ch);
        switch(ch) {
        case 'From Device':
            setDerivedIfChanged("");
            break;
        case 'custom':
            setChoice(ch);
            break;
        default:
            const expr = predefined[ch]?.expr;
            console.log("EXPR CH", expr);
            if(expr) {
                setDerivedIfChanged(JSON.stringify(stringToExprWithCron(expr)));
            }
        }
    };
    function changeEditDerived(newEditDerived) {
        if(newEditDerived === "") {
            setDerivedIfChanged("")
        } else {
            try {
                setDerivedIfChanged(JSON.stringify(JSON.parse(editDerived)));
            } catch {
                setEditDerived(newEditDerived);
                //todo: should be an error sign here
            }
        }
    };
    
    return (
        advanced ?
        <TextField
                variant="standard"
                label="JSON Formatted Raw Tag Value Formula (blank or empty for device sensor)"
                multiline
                fullWidth
                error={ derived !== editDerived }            
                type="string"
                value={ editDerived }
                disabled={ readOnly }
                InputLabelProps={{ sx: { fontSize: 12 } }}
                InputProps={{ sx: { fontSize: 12 } }}
                onChange={(event) => changeEditDerived(event.target.value)}
            />
        : (<>
            <FormControl fullWidth>
                <Select
                    labelId="choices-label"
                    size="small"
                    value={choice}
                    onChange={(e) => chooseChoice(e.target.value)}
                    disabled={ readOnly }                    
                >
                    <MenuItem value="From Device">From Device (no calculation)</MenuItem>
                    { predefined && predefined.map((v,idx) => { return (<MenuItem value={idx}>{v.name}</MenuItem>) }) }
                    <MenuItem value="custom">Custom...</MenuItem>
                    <MenuItem value="unknown" style={{ display: "none" }}>Loading...</MenuItem>                    
                </Select>
            </FormControl>
            { choice === "custom" && <TextField
                variant="standard"
                label="Tag Value Formula (blank for device sensor)"
                fullWidth
                multiline
                type="string"
                error={parseError}
                disabled={ readOnly }                                                             
                value={ parsed }
                InputLabelProps={{ sx: { fontSize: 12 } }}
                InputProps={{ sx: { fontSize: 12 } }}
                onChange={(event) => changeParsed(event.target.value)} />
            }
            <div>
                { Number.isInteger(choice) && <Typography variant="subtitle2" style={{ fontSize: 10 }}>
                                                  {predefined[choice]?.description || ""}
                                              </Typography> }
                { !!Object.keys(depParams).length &&
                  <table>
                      <tbody>
                          { Object.entries(depParams).map((kv) => <ParamChooser readOnly={readOnly} index={kv[0]} value={derivedParams[kv[0]] || null} param={kv[1] || null} handleParamChange={handleParamChange}/>) }
                      </tbody>
                  </table>
                }
            </div>
           </>)
    )
}

export default TagValueCalculation;

          
